import React from 'react';
import { SEO } from '@/components/SEO';
import { Link, useNavigate } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import Breadcrumbs from '@/components/ui/Breadcrumbs';
import LocationFAQ from '@/components/locations/LocationFAQ';
import LocationNeighborhoods from '@/components/locations/LocationNeighborhoods';
import LocationBlogLinks from '@/components/locations/LocationBlogLinks';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Phone, MapPin, Star, Droplets, Sparkles, Wind, Filter, Brush, Sun } from 'lucide-react';

const CamarilloPage = () => {
  const navigate = useNavigate();
  
  const locationSchema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": "Ventura Power Washing - Camarillo",
    "description": "Professional pressure washing, window cleaning, and carpet care services in Camarillo, CA. Eco-friendly cleaning solutions for residential and commercial properties.",
    "url": "https://www.venturapowerwashing.com/camarillo",
    "telephone": "(805) 464-7166",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Camarillo",
      "addressRegion": "CA",
      "postalCode": "93010",
      "addressCountry": "US"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": "34.2164",
      "longitude": "-119.0376"
    },
    "areaServed": {
      "@type": "City",
      "name": "Camarillo",
      "containedInPlace": {
        "@type": "State",
        "name": "California"
      }
    },
    "openingHoursSpecification": {
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
      "opens": "07:00",
      "closes": "19:00"
    },
    "priceRange": "$$",
    "image": "https://www.venturapowerwashing.com/og-image.jpg",
    "sameAs": [
      "https://www.facebook.com/venturapowerwashing",
      "https://www.instagram.com/venturacleaningco",
      "https://www.yelp.com/biz/ventura-power-washing-thousand-oaks"
    ]
  };

  const services = [
    {
      icon: <Droplets className="h-8 w-8 text-ventura-blue" />,
      title: "Pressure Washing",
      description: "Professional pressure washing for driveways, patios, and exterior surfaces.",
      link: "/services/pressure-washing"
    },
    {
      icon: <Sparkles className="h-8 w-8 text-ventura-blue" />,
      title: "Window Cleaning",
      description: "Crystal clear window cleaning that removes salt air residue and dust.",
      link: "/services/window-washing"
    },
    {
      icon: <Wind className="h-8 w-8 text-ventura-blue" />,
      title: "Carpet Cleaning",
      description: "Deep carpet cleaning that removes allergens and dirt.",
      link: "/services/carpet-cleaning"
    },
    {
      icon: <Filter className="h-8 w-8 text-ventura-blue" />,
      title: "Gutter Cleaning",
      description: "Keep gutters clear and protect your home from water damage.",
      link: "/services/gutter-cleaning"
    },
    {
      icon: <Brush className="h-8 w-8 text-ventura-blue" />,
      title: "Soft Wash",
      description: "Gentle cleaning for delicate surfaces like stucco and painted exteriors.",
      link: "/services/soft-wash"
    },
    {
      icon: <Sun className="h-8 w-8 text-ventura-blue" />,
      title: "Solar Panel Cleaning",
      description: "Maximize solar efficiency with professional panel maintenance.",
      link: "/services/solar-panel-cleaning"
    }
  ];

  const faqs = [
    {
      question: "How much does pressure washing cost in Camarillo, CA?",
      answer: "Most driveway, patio, and house pressure washing jobs in Camarillo run $150-$400, depending on square footage and surface type. We provide a free, no-obligation estimate before any work begins."
    },
    {
      question: "How much does window cleaning cost in Camarillo?",
      answer: "Residential window cleaning in Camarillo typically costs $150-$300 for a standard home, inside and out."
    },
    {
      question: "Do you offer carpet cleaning in Camarillo?",
      answer: "Yes. Whole-house carpet cleaning in Camarillo generally runs $100-$300 depending on square footage and the number of rooms. We use deep extraction methods that handle dust from surrounding agricultural land."
    },
    {
      question: "Is solar panel cleaning worth it for Camarillo homes?",
      answer: "Yes — Camarillo's coastal salt air and agricultural dust build up on panels quickly and can reduce efficiency by 15-25%. Professional solar panel cleaning in Camarillo typically costs $150-$350 and helps maximize energy production."
    }
  ];

  return (
    <div className="min-h-screen flex flex-col">
      <SEO
        title="Pressure Washing Camarillo CA | Window & Carpet Cleaning Services"
        description="Professional pressure washing, window cleaning & carpet care in Camarillo, CA. Serving 93010, 93011, 93012. Eco-friendly, licensed & insured. Free estimates!"
        keywords="pressure washing camarillo, window cleaning camarillo, carpet cleaning camarillo, power washing 93010, house washing camarillo"
        path="/camarillo"
      >
        <script type="application/ld+json">
          {JSON.stringify(locationSchema)}
        </script>
      </SEO>

      <Navbar />
      <Breadcrumbs />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-ventura-blue to-ventura-darkBlue text-white py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Expert Pressure Washing Services in Camarillo, CA
            </h1>
            <p className="text-xl md:text-2xl mb-8 opacity-90">
              Professional window cleaning, pressure washing, and carpet care for Camarillo residents and businesses in 93010, 93011, and 93012
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                className="bg-white text-ventura-blue hover:bg-gray-100 text-lg px-8 py-4"
                onClick={() => window.location.href = "tel:+18054647166"}
              >
                <Phone className="mr-2" size={20} />
                Call (805) 464-7166
              </Button>
              <Button 
                className="bg-ventura-green text-white hover:bg-green-600 text-lg px-8 py-4"
                onClick={() => navigate('/free-estimate')}
              >
                Get Free Camarillo Estimate
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="section-padding bg-white">
        <div className="container mx-auto">
          <h2 className="text-3xl font-bold text-center mb-12">
            Our Camarillo Cleaning Services
          </h2>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, index) => (
              <Card key={index} className="card-shadow h-full hover:shadow-lg transition-shadow duration-300">
                <CardContent className="p-6 text-center">
                  <div className="flex justify-center mb-4">
                    {service.icon}
                  </div>
                  <h3 className="text-xl font-bold mb-4">{service.title}</h3>
                  <p className="text-gray-600 mb-6">{service.description}</p>
                  <Link 
                    to={service.link}
                    className="text-ventura-blue hover:text-ventura-darkBlue font-semibold hover:underline"
                  >
                    Learn More →
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Local Area Information */}
      <section className="section-padding bg-ventura-lightGray">
        <div className="container mx-auto">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-center mb-8">
              What Makes Camarillo Properties Unique to Clean?
            </h2>
            <p className="text-lg text-gray-700 text-center mb-8">
              Camarillo sits in the heart of Ventura County's agricultural region, so properties here face a mix of coastal salt air
              and agricultural dust that inland-only cleaners aren't equipped for. Our local team uses techniques suited to both
              conditions to keep Camarillo homes and businesses looking their best year-round.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
              <div>
                <h3 className="text-2xl font-bold mb-4">Camarillo Areas We Serve</h3>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-center"><MapPin className="h-4 w-4 mr-2 text-ventura-blue" />Old Town Camarillo (93010)</li>
                  <li className="flex items-center"><MapPin className="h-4 w-4 mr-2 text-ventura-blue" />Camarillo Springs (93012)</li>
                  <li className="flex items-center"><MapPin className="h-4 w-4 mr-2 text-ventura-blue" />Spanish Hills</li>
                  <li className="flex items-center"><MapPin className="h-4 w-4 mr-2 text-ventura-blue" />Las Posas Estates</li>
                  <li className="flex items-center"><MapPin className="h-4 w-4 mr-2 text-ventura-blue" />Camarillo Heights</li>
                </ul>
              </div>
              
              <div>
                <h3 className="text-2xl font-bold mb-4">Camarillo Cleaning Specialists</h3>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-center"><Star className="h-4 w-4 mr-2 text-yellow-500" />Salt Air & Dust Removal</li>
                  <li className="flex items-center"><Star className="h-4 w-4 mr-2 text-yellow-500" />Agricultural Residue Cleaning</li>
                  <li className="flex items-center"><Star className="h-4 w-4 mr-2 text-yellow-500" />Eco-Friendly Solutions</li>
                  <li className="flex items-center"><Star className="h-4 w-4 mr-2 text-yellow-500" />Licensed & Insured</li>
                  <li className="flex items-center"><Star className="h-4 w-4 mr-2 text-yellow-500" />Local Camarillo Team</li>
                </ul>
              </div>
            </div>

            <div className="text-center">
              <p className="text-lg text-gray-700 mb-6">
                Located in the heart of Ventura County's agricultural region, Camarillo presents unique cleaning challenges from coastal salt air and agricultural dust. Our experienced team understands these local conditions and uses specialized techniques to keep your property looking its best year-round.
              </p>
              
              <Button 
                className="button-primary text-lg px-8 py-4"
                onClick={() => navigate('/free-estimate')}
              >
                Get Your Free Camarillo Cleaning Quote Today
              </Button>
            </div>
          </div>
        </div>
      </section>

      <LocationNeighborhoods
        city="Camarillo"
        neighborhoods={[
          { name: 'Spanish Hills', note: 'We serve Spanish Hills homes and HOA communities with soft washing for stucco, driveway and walkway cleaning, windows, gutters, and solar panels.' },
          { name: 'Camarillo Heights', note: 'We serve Camarillo Heights homeowners with hot-water pressure washing for driveways and patios, soft washing for stucco and roofs, and window and gutter cleaning.' },
        ]}
      />
      <LocationFAQ city="Camarillo" faqs={faqs} />
      <LocationBlogLinks city="Camarillo" />

      <Footer />
    </div>
  );
};

export default CamarilloPage;